import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Link as MuiLink,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  AutocompleteField,
  FormSelect,
  FormTextField,
  PageHeader,
} from '@/components';
import { INDUSTRY_OPTIONS } from '@/constants/options';
import { STATES, citiesOf, districtsOf } from '@/constants/india';
import { tenantApi } from '@/api/tenant';
import { normalizeError } from '@/api/client';
import { brand } from '@/theme/theme';
import type { Tenant, TenantProfileUpdateRequest } from '@/types';

/**
 * Edit Profile page (`/profile`).
 *
 * Fronts {@code GET /tenants/me} and {@code PUT /tenants/me}. Lets the
 * signed-in tenant edit anything they originally typed on registration
 * EXCEPT identity columns the server refuses to touch (code, business
 * type, status, subdomain). Those render as read-only summary chips at
 * the top.
 *
 * License certificates
 * --------------------
 * Drug License and Food (FSSAI) License certificates can be viewed,
 * removed, and replaced from this page via the new authenticated
 * endpoints under {@code /tenants/me/{kind}-certificate}. The legacy
 * registration-time upload (one-shot, token-protected) is unchanged —
 * this is the day-2 management flow.
 */

/** Allowed file extensions for certificate uploads. */
const ALLOWED_CERT_EXTS = ['.pdf', '.jpg', '.jpeg', '.png'];
const MAX_CERT_BYTES = 5 * 1024 * 1024;

interface ProfileFormValues {
  name: string;
  industry: string;
  gstin: string;
  drugLicenseNumber: string;
  drugLicenseExpiry: string;
  foodLicenseNumber: string;
  foodLicenseExpiry: string;
  address: string;
  area: string;
  state: string;
  district: string;
  city: string;
  pincode: string;
  phone: string;
  email: string;
}

/** Convert wire {@code Tenant} → form-friendly defaults (null → ''). */
function tenantToForm(t: Tenant): ProfileFormValues {
  return {
    name: t.name ?? '',
    industry: t.industry ?? '',
    gstin: t.gstin ?? '',
    drugLicenseNumber: t.drugLicenseNumber ?? '',
    drugLicenseExpiry: t.drugLicenseExpiry ?? '',
    foodLicenseNumber: t.foodLicenseNumber ?? '',
    foodLicenseExpiry: t.foodLicenseExpiry ?? '',
    address: t.address ?? '',
    area: t.area ?? '',
    state: t.state ?? '',
    district: t.district ?? '',
    city: t.city ?? '',
    pincode: t.pincode ?? '',
    phone: t.phone ?? '',
    email: t.email ?? '',
  };
}

/**
 * Build the PUT body. Sends every editable field on every save —
 * server-side, fields whose value is null are skipped, and we
 * convert blank strings to null so the user can clear a field.
 */
function formToPayload(
  values: ProfileFormValues,
  original: Tenant,
): TenantProfileUpdateRequest {
  const empty = (s: string) => (s === '' ? undefined : s);
  const body: TenantProfileUpdateRequest = {
    name: values.name.trim() || original.name,
    industry: empty(values.industry),
    gstin: empty(values.gstin),
    drugLicenseNumber: empty(values.drugLicenseNumber),
    foodLicenseNumber: empty(values.foodLicenseNumber),
    address: empty(values.address),
    area: empty(values.area),
    city: empty(values.city),
    district: empty(values.district),
    state: empty(values.state),
    pincode: empty(values.pincode),
    phone: empty(values.phone),
    email: empty(values.email),
  };

  // Drug License expiry: ''  → clear; non-empty → set; unchanged → no-op.
  if (values.drugLicenseExpiry !== (original.drugLicenseExpiry ?? '')) {
    if (values.drugLicenseExpiry === '') {
      body.clearDrugLicenseExpiry = true;
    } else {
      body.drugLicenseExpiry = values.drugLicenseExpiry;
    }
  }
  if (values.foodLicenseExpiry !== (original.foodLicenseExpiry ?? '')) {
    if (values.foodLicenseExpiry === '') {
      body.clearFoodLicenseExpiry = true;
    } else {
      body.foodLicenseExpiry = values.foodLicenseExpiry;
    }
  }
  return body;
}

// ---------------------------------------------------------------------------
// Certificate slot — view / remove / upload one license certificate.
//
// Reused twice on the page: once for Drug License, once for Food (FSSAI)
// License. The two slots share storage conventions and behaviour, with
// only the {@code kind} discriminator differing.
// ---------------------------------------------------------------------------

interface CertificateSlotProps {
  kind: 'dl' | 'food';
  label: string;
  filename?: string;
  onUpdated: (tenant: Tenant) => void;
  onError: (msg: string) => void;
}

function CertificateSlot({
  kind,
  label,
  filename,
  onUpdated,
  onError,
}: CertificateSlotProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Pending file-system action — disables the buttons while in flight.
  const [busy, setBusy] = useState<'upload' | 'remove' | null>(null);

  // Preview dialog state. The blob URL is created on demand and revoked
  // on close so we don't leak object URLs across viewings.
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewIsPdf, setPreviewIsPdf] = useState(false);

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewIsPdf(false);
  };

  // Revoke blob URL on unmount.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleView = async () => {
    if (!filename) return;
    setPreviewLoading(true);
    try {
      const url = await tenantApi.fetchCertificateBlobUrl(kind);
      // Heuristic for picking <embed> vs <img> — PDFs need a viewer,
      // images render directly. Fall back to <embed> for unknowns.
      const ext = filename.toLowerCase().split('.').pop() ?? '';
      setPreviewIsPdf(ext === 'pdf');
      setPreviewUrl(url);
    } catch (err) {
      const e = normalizeError(err);
      onError(e.message || 'Could not load certificate.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleRemove = async () => {
    if (!filename) return;
    if (!window.confirm(`Remove the uploaded ${label}? This cannot be undone.`)) {
      return;
    }
    setBusy('remove');
    try {
      const tenant = await tenantApi.removeCertificate(kind);
      onUpdated(tenant);
    } catch (err) {
      const e = normalizeError(err);
      onError(e.message || 'Could not remove certificate.');
    } finally {
      setBusy(null);
    }
  };

  const handleFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset the input so picking the same filename twice in a row still
    // triggers onChange.
    e.target.value = '';
    if (!file) return;

    // Client-side guards mirror the backend's checks. Catching them
    // here gives the user a faster, friendlier error.
    const lowered = file.name.toLowerCase();
    if (!ALLOWED_CERT_EXTS.some((ext) => lowered.endsWith(ext))) {
      onError('Unsupported file type. Allowed: PDF, JPG, JPEG, PNG.');
      return;
    }
    if (file.size > MAX_CERT_BYTES) {
      onError('File exceeds the 5 MB limit.');
      return;
    }

    setBusy('upload');
    try {
      const tenant = await tenantApi.uploadCertificate(kind, file);
      onUpdated(tenant);
    } catch (err) {
      const e = normalizeError(err);
      onError(e.message || 'Could not upload certificate.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 1.5,
        background: '#FAFAFC',
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={1}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="caption" sx={{ color: brand.inkSoft }}>
            {label}
          </Typography>
          {filename ? (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: brand.violet,
                wordBreak: 'break-all',
              }}
            >
              {filename}
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ color: brand.inkSoft }}>
              Not uploaded yet.
            </Typography>
          )}
        </Box>

        <Stack direction="row" spacing={1} flexWrap="wrap">
          {filename && (
            <Button
              size="small"
              variant="outlined"
              startIcon={
                previewLoading ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <VisibilityIcon fontSize="small" />
                )
              }
              onClick={handleView}
              disabled={previewLoading || busy !== null}
            >
              View
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={
              busy === 'upload' ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <CloudUploadIcon fontSize="small" />
              )
            }
            onClick={() => fileInputRef.current?.click()}
            disabled={busy !== null}
          >
            {filename ? 'Replace' : 'Upload'}
          </Button>
          {filename && (
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={
                busy === 'remove' ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <DeleteIcon fontSize="small" />
                )
              }
              onClick={handleRemove}
              disabled={busy !== null}
            >
              Remove
            </Button>
          )}
        </Stack>
      </Stack>

      <input
        ref={fileInputRef}
        type="file"
        hidden
        accept={ALLOWED_CERT_EXTS.join(',')}
        onChange={handleFileChosen}
      />

      <Dialog open={previewUrl !== null} onClose={closePreview} maxWidth="md" fullWidth>
        <DialogTitle>{filename || label}</DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 480,
            background: '#000',
          }}
        >
          {previewUrl &&
            (previewIsPdf ? (
              <embed
                src={previewUrl}
                type="application/pdf"
                width="100%"
                height="640"
              />
            ) : (
              <img
                src={previewUrl}
                alt={filename || label}
                style={{ maxWidth: '100%', maxHeight: 640 }}
              />
            ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePreview}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// ProfilePage
// ---------------------------------------------------------------------------

export function ProfilePage() {
  const navigate = useNavigate();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedToast, setSavedToast] = useState(false);
  const [certToast, setCertToast] = useState<string | null>(null);
  const [certError, setCertError] = useState<string | null>(null);

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<ProfileFormValues>({
    defaultValues: {
      name: '',
      industry: '',
      gstin: '',
      drugLicenseNumber: '',
      drugLicenseExpiry: '',
      foodLicenseNumber: '',
      foodLicenseExpiry: '',
      address: '',
      area: '',
      state: '',
      district: '',
      city: '',
      pincode: '',
      phone: '',
      email: '',
    },
  });

  // State drives district/city options (same pattern as RegisterTenantPage).
  const watchedState = useWatch({ control, name: 'state' }) ?? '';
  const districtOptions = useMemo(() => districtsOf(watchedState), [watchedState]);
  const cityOptions = useMemo(() => citiesOf(watchedState), [watchedState]);

  // Track the previous state so dependent autocompletes only reset on a
  // genuine user-driven change — NOT on the initial hydration from the
  // server, which would silently clobber the saved district/city.
  const previousState = useRef<string | null>(null);
  useEffect(() => {
    if (previousState.current === null) {
      previousState.current = watchedState;
      return;
    }
    if (previousState.current !== watchedState) {
      previousState.current = watchedState;
      setValue('district', '', { shouldValidate: false });
      setValue('city', '', { shouldValidate: false });
    }
  }, [watchedState, setValue]);

  // Load tenant on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const t = await tenantApi.getMe();
        if (cancelled) return;
        setTenant(t);
        reset(tenantToForm(t));
        previousState.current = t.state ?? '';
      } catch (err) {
        if (cancelled) return;
        const e = normalizeError(err);
        setLoadError(e.message || 'Could not load profile');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reset]);

  const onSubmit = async (values: ProfileFormValues) => {
    if (!tenant) return;
    setSaving(true);
    setSaveError(null);
    try {
      const body = formToPayload(values, tenant);
      const updated = await tenantApi.updateMe(body);
      setTenant(updated);
      reset(tenantToForm(updated));
      setSavedToast(true);
    } catch (err) {
      const e = normalizeError(err);
      setSaveError(e.message || 'Could not save profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError || !tenant) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError ?? 'Could not load profile.'}
        </Alert>
        <Button onClick={() => navigate('/dashboard')} startIcon={<ArrowBackIcon />}>
          Back to dashboard
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ color: brand.inkSoft }}
        >
          Back
        </Button>
      </Stack>

      <PageHeader
        title="Edit profile"
        subtitle="Update your business details, regulatory licences and contact information."
      />

      {/* Read-only identity strip — fields the backend won't let us change. */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            divider={<Divider orientation="vertical" flexItem />}
          >
            <Box>
              <Typography variant="caption" sx={{ color: brand.inkSoft }}>
                Tenant code
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {tenant.code}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: brand.inkSoft }}>
                Business type
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {tenant.tenantType}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: brand.inkSoft }}>
                Status
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {tenant.status}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      )}
      {certError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCertError(null)}>
          {certError}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Stack spacing={3}>
              {/* ----- Business details ----- */}
              <Box>
                <Typography variant="h4" sx={{ mb: 1.5, color: brand.navy }}>
                  Business details
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12}>
                    <FormTextField
                      name="name"
                      register={register}
                      errors={errors}
                      label="Business name"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormSelect
                      name="industry"
                      control={control}
                      errors={errors}
                      label="Industry"
                      options={[...INDUSTRY_OPTIONS]}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormTextField
                      name="gstin"
                      register={register}
                      errors={errors}
                      label="GSTIN"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormTextField
                      name="phone"
                      register={register}
                      errors={errors}
                      label="Phone"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormTextField
                      name="email"
                      register={register}
                      errors={errors}
                      label="Business email"
                      type="email"
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* ----- Regulatory licences ----- */}
              <Box>
                <Typography variant="h4" sx={{ mb: 0.5, color: brand.navy }}>
                  Regulatory licences
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: brand.inkSoft, display: 'block', mb: 1.5 }}
                >
                  Leave blank if not applicable. Allowed certificate
                  formats: PDF, JPG, JPEG, PNG (max 5 MB).
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <FormTextField
                      name="drugLicenseNumber"
                      register={register}
                      errors={errors}
                      label="Drug License Number"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormTextField
                      name="drugLicenseExpiry"
                      register={register}
                      errors={errors}
                      label="Drug License Expiry"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <CertificateSlot
                      kind="dl"
                      label="Drug License certificate"
                      filename={tenant.dlCertificateFilename}
                      onUpdated={(t) => {
                        setTenant(t);
                        setCertToast('Drug License certificate updated.');
                      }}
                      onError={setCertError}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormTextField
                      name="foodLicenseNumber"
                      register={register}
                      errors={errors}
                      label="Food License Number (FSSAI)"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormTextField
                      name="foodLicenseExpiry"
                      register={register}
                      errors={errors}
                      label="Food License Expiry"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <CertificateSlot
                      kind="food"
                      label="Food License (FSSAI) certificate"
                      filename={tenant.foodLicenseCertificateFilename}
                      onUpdated={(t) => {
                        setTenant(t);
                        setCertToast('Food License certificate updated.');
                      }}
                      onError={setCertError}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* ----- Address ----- */}
              <Box>
                <Typography variant="h4" sx={{ mb: 1.5, color: brand.navy }}>
                  Address
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12}>
                    <FormTextField
                      name="address"
                      register={register}
                      errors={errors}
                      label="Street address"
                      multiline
                      minRows={2}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormTextField
                      name="area"
                      register={register}
                      errors={errors}
                      label="Area / Locality"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <AutocompleteField
                      name="state"
                      control={control}
                      errors={errors}
                      label="State"
                      options={STATES}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <AutocompleteField
                      name="district"
                      control={control}
                      errors={errors}
                      label="District"
                      options={districtOptions}
                      disabled={!watchedState}
                      helperText={!watchedState ? 'Choose a state first' : undefined}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <AutocompleteField
                      name="city"
                      control={control}
                      errors={errors}
                      label="City / town"
                      options={cityOptions}
                      disabled={!watchedState}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormTextField
                      name="pincode"
                      register={register}
                      errors={errors}
                      label="Pincode"
                    />
                  </Grid>
                </Grid>
              </Box>

              <Stack
                direction="row"
                spacing={1.5}
                justifyContent="flex-end"
                sx={{ mt: 1 }}
              >
                <MuiLink
                  component="button"
                  type="button"
                  onClick={() => reset(tenantToForm(tenant))}
                  sx={{ color: brand.inkSoft, fontSize: 14 }}
                >
                  Reset
                </MuiLink>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={saving}
                  startIcon={
                    saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />
                  }
                  sx={{ minWidth: 160 }}
                >
                  {saving ? 'Saving…' : 'Save changes'}
                </Button>
              </Stack>
            </Stack>
          </form>
        </CardContent>
      </Card>

      <Snackbar
        open={savedToast}
        autoHideDuration={3000}
        onClose={() => setSavedToast(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSavedToast(false)}>
          Profile updated.
        </Alert>
      </Snackbar>

      <Snackbar
        open={certToast !== null}
        autoHideDuration={3000}
        onClose={() => setCertToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setCertToast(null)}>
          {certToast}
        </Alert>
      </Snackbar>
    </Box>
  );
}
